'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import TeamLogo from '@/components/ui/TeamLogo';
import ConfidenceBadge from '@/components/ui/ConfidenceBadge';

import { getTeamAbbr, getTeamColor, mapGameStatus } from '@/lib/apiHockey/client';

interface Match {
  id: string;
  time: string;
  venue: string;
  homeTeam: { name: string; abbr: string; color: string; record: string };
  awayTeam: { name: string; abbr: string; color: string; record: string };
  homeXG: number;
  awayXG: number;
  homeCorsi: number;
  awayCorsi: number;
  homePP: number;
  awayPP: number;
  homePK: number;
  awayPK: number;
  homeGoalie: string;
  awayGoalie: string;
  homeWinProb: number;
  awayWinProb: number;
  confidence: number;
  projectedScore: string;
  fatigue: boolean;
  injuries: string[];
  conference: 'Est' | 'Ouest';
  status: 'live' | 'scheduled' | 'final';
  homeScore?: number;
  awayScore?: number;
}

type SortKey = 'time' | 'confidence' | 'homeWinProb';

function formatGameTime(dateStr: string, timeStr: string): string {
  try {
    const dt = new Date(`${dateStr}T${timeStr}`);
    return dt.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET';
  } catch {
    return timeStr || '—';
  }
}

// Rough conference assignment by team
const EAST_TEAMS = new Set(['BOS','BUF','CAR','CBJ','DET','FLA','MTL','NJD','NYI','NYR','OTT','PHI','PIT','TBL','TOR','WSH']);

export default function MatchTableWrapper() {
  const [conference, setConference] = useState<'Tous' | 'Est' | 'Ouest'>('Tous');
  const [sortKey, setSortKey] = useState<SortKey>('confidence');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  const fetchMatches = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch('/api/hockey?action=today');
      const json = await res.json();

      if (!json.success) {
        throw new Error(json.error || 'API error');
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const games: any[] = json.data || [];

      const mapped: Match[] = games.map((g) => {
        const homeAbbr = getTeamAbbr(g.teams?.home?.name || '');
        const awayAbbr = getTeamAbbr(g.teams?.away?.name || '');
        const status = mapGameStatus(g.status?.short || 'NS');
        const conf: 'Est' | 'Ouest' = EAST_TEAMS.has(homeAbbr) ? 'Est' : 'Ouest';

        // Derive win probability from scores when live, else 50/50 with slight home advantage
        let homeWinProb = 53;
        let awayWinProb = 47;
        if (status === 'live') {
          const homeGoals = g.scores?.home?.total ?? 0;
          const awayGoals = g.scores?.away?.total ?? 0;
          const diff = homeGoals - awayGoals;
          homeWinProb = Math.min(90, Math.max(10, 53 + diff * 8));
          awayWinProb = 100 - homeWinProb;
        } else if (status === 'final') {
          homeWinProb = (g.scores?.home?.total ?? 0) > (g.scores?.away?.total ?? 0) ? 100 : 0;
          awayWinProb = 100 - homeWinProb;
        }

        return {
          id: String(g.id),
          time: formatGameTime(g.date, g.time),
          venue: g.venue || '—',
          homeTeam: {
            name: g.teams?.home?.name || '—',
            abbr: homeAbbr,
            color: getTeamColor(homeAbbr),
            record: '—',
          },
          awayTeam: {
            name: g.teams?.away?.name || '—',
            abbr: awayAbbr,
            color: getTeamColor(awayAbbr),
            record: '—',
          },
          homeXG: 0,
          awayXG: 0,
          homeCorsi: 50,
          awayCorsi: 50,
          homePP: 20,
          awayPP: 20,
          homePK: 80,
          awayPK: 80,
          homeGoalie: '—',
          awayGoalie: '—',
          homeWinProb,
          awayWinProb,
          confidence: status === 'live' ? 75 : 55,
          projectedScore: status === 'final'
            ? `${g.scores?.home?.total ?? 0}–${g.scores?.away?.total ?? 0}`
            : '—',
          fatigue: false,
          injuries: [],
          conference: conf,
          status,
          homeScore: g.scores?.home?.total ?? undefined,
          awayScore: g.scores?.away?.total ?? undefined,
        };
      });

      setMatches(mapped);
      setLastUpdated(Date.now());
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMatches();
    const interval = setInterval(fetchMatches, 30000);
    return () => clearInterval(interval);
  }, [fetchMatches]);

  const filtered = matches
    .filter((m) => conference === 'Tous' || m.conference === conference)
    .sort((a, b) => {
      const mult = sortDir === 'desc' ? -1 : 1;
      return (a[sortKey] > b[sortKey] ? 1 : -1) * mult;
    });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  if (loading) {
    return (
      <div className="card p-8 flex items-center justify-center gap-3 text-muted-foreground">
        <Icon name="ArrowPathIcon" size={18} className="animate-spin text-primary" />
        <span className="text-sm">Chargement des matchs NHL depuis API-Hockey...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="ExclamationTriangleIcon" size={18} className="text-warning" />
          <span className="text-sm font-semibold text-foreground">Erreur API-Hockey</span>
        </div>
        <p className="text-xs text-muted-foreground mb-4">{error}</p>
        <button onClick={fetchMatches} className="btn-ghost text-xs flex items-center gap-2">
          <Icon name="ArrowPathIcon" size={14} />
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      {/* Filters */}
      <div className="flex items-center justify-between gap-3 p-4 border-b border-border flex-wrap">
        <div className="flex items-center gap-2">
          {(['Tous', 'Est', 'Ouest'] as const).map((conf) => (
            <button
              key={`conf-${conf}`}
              onClick={() => setConference(conf)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-all duration-150 ${
                conference === conf
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
              }`}
            >
              {conf}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-positive pulse-dot" />
            <span className="font-mono-data text-positive">API-Hockey LIVE</span>
          </div>
          {lastUpdated && (
            <span className="font-mono-data">
              Sync: {new Date(lastUpdated).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <Icon name="FunnelIcon" size={14} />
          <span>{filtered.length} match{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground text-sm">
          Aucun match NHL aujourd&apos;hui pour la conférence sélectionnée.
        </div>
      ) : (
        /* Table */
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="border-b border-border bg-muted/30">
              <tr>
                <th className="table-header w-10">#</th>
                <th className="table-header">Heure</th>
                <th className="table-header">Équipe Domicile</th>
                <th className="table-header">Équipe Extérieur</th>
                <th
                  className="table-header cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('confidence')}
                >
                  <div className="flex items-center gap-1">
                    Confiance
                    <Icon name={sortKey === 'confidence' ? (sortDir === 'desc' ? 'ChevronDownIcon' : 'ChevronUpIcon') : 'ChevronUpDownIcon'} size={12} />
                  </div>
                </th>
                <th
                  className="table-header cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('homeWinProb')}
                >
                  <div className="flex items-center gap-1">
                    Win% Dom.
                    <Icon name={sortKey === 'homeWinProb' ? (sortDir === 'desc' ? 'ChevronDownIcon' : 'ChevronUpIcon') : 'ChevronUpDownIcon'} size={12} />
                  </div>
                </th>
                <th className="table-header">Score</th>
                <th className="table-header">Statut</th>
                <th className="table-header">Lieu</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((match, idx) => {
                const expanded = expandedId === match.id;
                return (
                  <React.Fragment key={match.id}>
                    <tr
                      className={`border-b border-border row-hover cursor-pointer transition-colors ${expanded ? 'bg-primary/5' : ''}`}
                      onClick={() => setExpandedId(expanded ? null : match.id)}
                    >
                      <td className="table-cell text-muted-foreground font-mono-data text-xs text-center">{idx + 1}</td>
                      <td className="table-cell">
                        <span className="text-xs font-mono-data text-foreground">{match.time}</span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <TeamLogo abbreviation={match.homeTeam.abbr} color={match.homeTeam.color} size="sm" />
                          <div>
                            <p className="text-sm font-medium text-foreground leading-tight">{match.homeTeam.abbr}</p>
                            <p className="text-2xs text-muted-foreground">{match.homeTeam.name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <TeamLogo abbreviation={match.awayTeam.abbr} color={match.awayTeam.color} size="sm" />
                          <div>
                            <p className="text-sm font-medium text-foreground leading-tight">{match.awayTeam.abbr}</p>
                            <p className="text-2xs text-muted-foreground">{match.awayTeam.name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <ConfidenceBadge value={match.confidence} showLabel={false} />
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold font-mono-data text-foreground">{match.homeWinProb}%</span>
                          <div className="w-12 h-1.5 rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${match.homeWinProb}%`, backgroundColor: match.homeTeam.color }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        {match.status === 'scheduled' ? (
                          <span className="text-xs text-muted-foreground font-mono-data">—</span>
                        ) : (
                          <span className="text-sm font-bold font-mono-data text-foreground">
                            {match.homeScore ?? 0}–{match.awayScore ?? 0}
                          </span>
                        )}
                      </td>
                      <td className="table-cell">
                        <span className={`status-badge text-2xs ${
                          match.status === 'live' ? 'bg-positive-subtle text-positive' :
                          match.status === 'final' ? 'bg-muted text-muted-foreground' :
                          'bg-info-subtle text-primary'
                        }`}>
                          {match.status === 'live' ? '● LIVE' : match.status === 'final' ? 'FINAL' : 'PRÉVU'}
                        </span>
                      </td>
                      <td className="table-cell">
                        <span className="text-xs text-muted-foreground truncate max-w-[120px] block">{match.venue}</span>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="border-b border-border bg-primary/3">
                        <td colSpan={9} className="px-4 py-3">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                            <div>
                              <p className="text-muted-foreground mb-1">Conférence</p>
                              <p className="font-medium text-foreground">{match.conference}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground mb-1">Win% Extérieur</p>
                              <p className="font-mono-data text-foreground">{match.awayWinProb}%</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground mb-1">Score Projeté</p>
                              <p className="font-mono-data text-foreground">{match.projectedScore}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground mb-1">Source</p>
                              <p className="font-mono-data text-positive">API-Hockey LIVE</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}